import { useMemo, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useApiQuery } from '../../../../hooks/useApiQuery.js'
import { useMarketplaceParams } from '../../hooks/useMarketplaceParams.js'
import { useMarketplaceCounts } from '../../hooks/useMarketplaceCounts.js'
import {
  POLLING_INTERVAL,
  getFilterMeta,
} from '../../utils/marketplaceConstants.js'

/**
 * useMarketplaceSidebarLogic
 *
 * Hook for MarketplaceSidebar component.
 * Fetches category counts and manages sidebar state.
 */
export const useMarketplaceSidebarLogic = ({
  isDrayNow = false,
  isAdmin = false,
  showStreetTurns = false,
} = {}) => {
  const location = useLocation()
  const isMarketplaceRoute = location.pathname.startsWith('/marketplace')

  const {
    markets,
    marketsCsv,
    category,
    search,
    listType,
    setCategory,
    buildMarketplaceUrl,
    adminFiltersCount,
  } = useMarketplaceParams()

  // Category counts — single source of truth (shared with overview dropdown
  // and the search-redirect via React Query cache dedup).
  const {
    counts,
    isLoading: isLoadingCounts,
    isFetching: isFetchingCounts,
    refetch: refetchCounts,
  } = useMarketplaceCounts()

  // Build params for street turn count
  const streetTurnParams = useMemo(
    () => ({
      marketUids: markets.length > 0 ? markets : undefined,
    }),
    [markets],
  )

  // Fetch street turn count with polling (admin only when feature enabled)
  const { data: streetTurnData } = useApiQuery({
    key: ['getOpenStreetTurnMatchCount', streetTurnParams],
    payload: streetTurnParams,
    enabled: isAdmin && showStreetTurns,
    staleTime: 30000,
    poll: POLLING_INTERVAL,
    showToast: false,
  })

  // Extract street turn count from response
  const streetTurnCount = streetTurnData?.count ?? null

  // Get filter metadata based on host
  const filterMeta = useMemo(() => getFilterMeta(isDrayNow), [isDrayNow])

  // Build categories with counts
  const categories = useMemo(() => {
    const meta = filterMeta
    const countData = counts || {}

    return Object.keys(meta)
      .filter((key) => key !== 'overview' && key !== 'search') // Exclude non-category items
      .filter((key) => Object.prototype.hasOwnProperty.call(countData, key)) // Only show categories returned by API
      .map((key) => ({
        key,
        title: meta[key].title,
        icon: meta[key].icon,
        count: countData[key] ?? 0,
        isActive: isMarketplaceRoute && key === category,
        url: buildMarketplaceUrl({ category: key, page: 1 }),
      }))
  }, [filterMeta, counts, category, isMarketplaceRoute, buildMarketplaceUrl])

  // Handle category selection
  const handleCategorySelect = useCallback(
    (categoryKey) => {
      setCategory(categoryKey)
    },
    [setCategory],
  )

  // Check if any filters are applied
  const hasAppliedFilters = useMemo(() => {
    return adminFiltersCount > 0
  }, [adminFiltersCount])

  // Build create order URL
  const createOrderUrl = '/order/new'

  // Build street turns URL
  const streetTurnsUrl = '/streetturns'

  return {
    // State
    categories,
    currentCategory: category,
    listType,
    marketsCsv,
    search,

    // Loading states
    isLoading: isLoadingCounts,
    isFetching: isFetchingCounts,

    // Filter state
    hasAppliedFilters,
    filterCount: adminFiltersCount,

    // URLs
    createOrderUrl,
    streetTurnsUrl,

    // Street turn count
    streetTurnCount,

    // Actions
    handleCategorySelect,
    refetchCounts,
  }
}
